import React, { useEffect } from 'react';
import { Plus, Layers } from 'lucide-react';
import { YSeriesCard } from './YSeriesCard';
import { ChartConfig, MeasureConfig } from '../../types';
import { generateUUID } from '../../utils/uuid';
import { resolveSeriesColors } from '../../utils/resolveSeriesColors';
import { formatSeriesLabel } from '../../utils/formatFieldLabel';
import { COLOR_PALETTES, DEFAULT_PALETTE, CHART_COLORS } from '../../constants';

interface BasicMappingZoneProps {
    config: ChartConfig;
    onUpdateConfig: (updates: Partial<ChartConfig>) => void;
    allCols: { label: string; value: string; isNumeric?: boolean }[];
    numericCols: { label: string; value: string }[];
}

export const BasicMappingZone: React.FC<BasicMappingZoneProps> = ({
    config,
    onUpdateConfig,
    allCols,
    numericCols
}) => {
    const { type, xField, measures = [], ySeries } = config;

    // Migration Effect: Convert legacy ySeries to measures if needed
    useEffect(() => {
        if ((!measures || measures.length === 0) && ySeries && ySeries.length > 0) {
            const migratedMeasures: MeasureConfig[] = ySeries.map(s => ({
                id: generateUUID(),
                field: s.field,
                aggregation: (s.aggregation as any) || 'sum',
                displayName: s.label || formatSeriesLabel(s.field, s.aggregation),
                isCustomLabel: !!s.label,
                colorMode: s.color && s.color !== 'auto' ? 'custom' : 'auto',
                color: s.color !== 'auto' ? s.color : undefined,
                visible: s.visible ?? true
            }));
            onUpdateConfig({ measures: migratedMeasures });
        }
    }, [ySeries, measures, onUpdateConfig]);

    // Don't show X-Axis for number charts
    const showXAxis = !['number'].includes(type || '');

    // Resolve colors for preview
    const currentPalette = config.customization?.paletteName === 'custom' && config.customization?.customPalette
        ? config.customization.customPalette
        : (COLOR_PALETTES[config.customization?.paletteName || DEFAULT_PALETTE] || CHART_COLORS);

    const colorMap = resolveSeriesColors(measures, currentPalette);

    const handleMeasureChange = (index: number, updates: Partial<MeasureConfig>) => {
        const newMeasures = [...measures];
        newMeasures[index] = { ...newMeasures[index], ...updates };

        console.log('[BasicMappingZone] Measure updated:', {
            index,
            updates,
            newMeasure: newMeasures[index]
        });

        onUpdateConfig({ measures: newMeasures });
    };

    const handleAddMeasure = () => {
        const newMeasure: MeasureConfig = {
            id: generateUUID(),
            field: '',
            aggregation: 'sum',
            displayName: '',
            isCustomLabel: false,
            colorMode: 'auto',
            visible: true
        };
        onUpdateConfig({ measures: [...measures, newMeasure] });
    };

    const handleDeleteMeasure = (index: number) => {
        const newMeasures = measures.filter((_, i) => i !== index);
        onUpdateConfig({ measures: newMeasures });
    };

    return (
        <div className="space-y-4 p-4 bg-obsidian-900/50 rounded-xl border border-gold-500/10">
            <h4 className="text-xs font-bold text-gold-500 uppercase tracking-widest flex items-center gap-2">
                <Layers className="w-3.5 h-3.5" /> Data Mapping
            </h4>

            {/* X-Axis */}
            {showXAxis && (
                <div className="space-y-1">
                    <label className="text-xs font-semibold text-gray-400">X-Axis</label>
                    <select
                        value={xField || ''}
                        onChange={(e) => onUpdateConfig({ xField: e.target.value })}
                        className="w-full text-sm px-2 py-1.5 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200 focus:ring-2 focus:ring-gold-500/50"
                    >
                        <option value="">Select Field</option>
                        {(type === 'scatter' ? numericCols : allCols).map(col => (
                            <option key={col.value} value={col.value}>
                                {col.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Measures (Y-Series) */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-gray-400">Measures</label>
                    <button
                        onClick={handleAddMeasure}
                        className="text-[10px] flex items-center gap-1 bg-gold-500/10 text-gold-400 px-2 py-1 rounded hover:bg-gold-500/20 transition-colors border border-gold-500/20"
                    >
                        <Plus className="w-3 h-3" /> Add Measure
                    </button>
                </div>

                <div className="space-y-2">
                    {measures.length === 0 && (
                        <div className="p-4 border border-dashed border-gold-500/10 rounded-lg text-center text-xs text-gray-500">
                            No measures added. Click "Add Measure" to visualize data.
                        </div>
                    )}

                    {measures.map((measure, idx) => (
                        <YSeriesCard
                            key={measure.id}
                            measure={measure}
                            index={idx}
                            onChange={handleMeasureChange}
                            onDelete={handleDeleteMeasure}
                            numericCols={numericCols}
                            resolvedColor={colorMap.get(measure.id)}
                            palette={currentPalette}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};
